/**
 * Reglas legales que comparten el servidor y el navegador.
 *
 * Vive en `shared/` por lo mismo que la lista de países: el servidor decide
 * con estas reglas y las páginas de Términos y Privacidad las MUESTRAN con
 * estas mismas. Si cada lado tuviera su copia, la página podría prometer una
 * edad y el registro aplicar otra.
 */

/**
 * Versión de los Términos y la Política de privacidad.
 *
 * Es la fecha de la última redacción. Cambiarla obliga a toda cuenta a volver
 * a aceptarlos al entrar: súbela solo cuando el cambio sea de fondo (qué datos
 * se recogen, para qué, con quién se comparten, qué se permite), no por una
 * errata.
 */
export const TERMS_VERSION = '2026-10-03';

/**
 * Quién responde por Open Dahu.
 *
 * TODO antes de publicar: rellenar con los datos reales. Las leyes de datos
 * (RGPD art. 13, LGPD, Ley 172-13…) exigen identificar al responsable y dar
 * una vía de contacto que de verdad se lea.
 */
export const OPERATOR = {
  /** Persona o empresa responsable del servicio y de los datos. */
  name: '[NOMBRE DEL RESPONSABLE]',
  /** País desde el que se opera. Fija la ley aplicable y los tribunales. */
  country: '[PAÍS DEL RESPONSABLE]',
  /** Buzón para privacidad, borrado de datos y denuncias de contenido. */
  email: '[CORREO DE CONTACTO]',
};

/**
 * Edad a partir de la cual alguien puede crear una cuenta y aceptar los
 * términos por sí mismo, según el país que declara.
 *
 * Es la "edad de consentimiento digital" de cada ley de datos:
 *
 *  - Unión Europea y EEE: el RGPD (art. 8) fija 16 y deja a cada estado
 *    bajarla hasta 13. Aquí va la que eligió cada uno.
 *  - Reino Unido: 13 (UK GDPR).
 *  - Estados Unidos: 13 (COPPA).
 *  - Brasil: 16. La LGPD exige consentimiento de los padres por debajo de 12 y
 *    el ECA Digital (Ley 15.211/2025) endurece el trato a menores de 16.
 *  - India: 18 (DPDP Act: "niño" es todo menor de edad).
 *  - Colombia: 18 (Ley 1581: los datos de menores requieren autorización de su
 *    representante).
 *  - Perú, China, Corea del Sur, Canadá (por Quebec): 14.
 *  - Australia: 16, por la ley de edad mínima en redes sociales de 2024.
 *
 * Cualquier país que no esté aquí usa DEFAULT_MIN_AGE. Es deliberado que el
 * valor por defecto sea el estricto habitual (13) y que bajar de ahí exija
 * añadir el país a mano: así un país nuevo nunca queda más expuesto sin que
 * alguien lo haya decidido.
 *
 * No es asesoría legal: conviene que un abogado revise la tabla antes de
 * apoyarse en ella, y cada vez que cambie una ley.
 */
export const DEFAULT_MIN_AGE = 13;

const CONSENT_AGE: Record<string, number> = {
  // Unión Europea
  AT: 14, BE: 13, BG: 14, HR: 16, CY: 14, CZ: 15, DK: 13, EE: 13, FI: 13,
  FR: 15, DE: 16, GR: 15, HU: 16, IE: 16, IT: 14, LV: 13, LT: 14, LU: 16,
  MT: 13, NL: 16, PL: 16, PT: 13, RO: 16, SK: 16, SI: 15, ES: 14, SE: 13,
  // Espacio Económico Europeo y Suiza
  IS: 13, LI: 16, NO: 13, CH: 13,
  // Resto
  GB: 13,
  US: 13,
  BR: 16,
  IN: 18,
  CO: 18,
  PE: 14,
  CN: 14,
  KR: 14,
  CA: 14,
  AU: 16,
};

/**
 * Países donde, por debajo de su edad de consentimiento, se admite una cuenta
 * CON permiso de la madre, el padre o el tutor legal, y desde qué edad.
 *
 * Solo entran países cuya ley no exige una verificación del permiso que esta
 * plataforma no puede hacer (como la COPPA o el RGPD). La declaración del
 * adulto se guarda con fecha en la cuenta.
 *
 * República Dominicana: la Ley 172-13 no fija una edad digital propia y el
 * Código del Menor (Ley 136-03) pide que actúen sus representantes, que es lo
 * que recoge la declaración. Pendiente de confirmar con un abogado dominicano.
 */
const WITH_GUARDIAN_FROM: Record<string, number> = {
  DO: 10,
};

export interface AgeRule {
  /** Edad desde la que se puede tener cuenta sin permiso de nadie. */
  alone: number;
  /** Edad mínima absoluta con permiso del tutor, o null si no se admite. */
  withGuardian: number | null;
}

export function ageRuleFor(country: string | null | undefined): AgeRule {
  const code = String(country ?? '').toUpperCase();
  const alone = CONSENT_AGE[code] ?? DEFAULT_MIN_AGE;
  const guardian = WITH_GUARDIAN_FROM[code];
  return { alone, withGuardian: guardian !== undefined && guardian < alone ? guardian : null };
}

/**
 * La regla más estricta de varias.
 *
 * Se usa cuando la conexión delata un país distinto del declarado: quien vive
 * en Alemania no se libra de la regla alemana eligiendo otro país en la lista.
 */
export function strictestRule(...countries: (string | null | undefined)[]): AgeRule {
  const rules = countries.filter(Boolean).map(ageRuleFor);
  if (rules.length === 0) return ageRuleFor(null);
  const alone = Math.max(...rules.map((r) => r.alone));
  // El permiso del tutor solo vale si TODOS los países implicados lo admiten.
  const guardian = rules.every((r) => r.withGuardian !== null)
    ? Math.max(...rules.map((r) => r.withGuardian!))
    : null;
  return { alone, withGuardian: guardian !== null && guardian < alone ? guardian : null };
}

/** Años cumplidos a día de hoy, con la fecha en `AAAA-MM-DD`. */
export function ageFrom(birthDate: string): number {
  const born = new Date(`${birthDate}T00:00:00Z`);
  const today = new Date();
  let age = today.getUTCFullYear() - born.getUTCFullYear();
  const month = today.getUTCMonth() - born.getUTCMonth();
  // Aún no ha llegado su cumpleaños este año.
  if (month < 0 || (month === 0 && today.getUTCDate() < born.getUTCDate())) age--;
  return age;
}

export type AgeVerdict =
  | { ok: true; needsGuardian: false }
  | { ok: true; needsGuardian: true; alone: number }
  | { ok: false; min: number };

/** Qué hace falta para que alguien de esa edad tenga cuenta con esa regla. */
export function judgeAge(age: number, rule: AgeRule): AgeVerdict {
  if (age >= rule.alone) return { ok: true, needsGuardian: false };
  if (rule.withGuardian !== null && age >= rule.withGuardian) {
    return { ok: true, needsGuardian: true, alone: rule.alone };
  }
  return { ok: false, min: rule.withGuardian ?? rule.alone };
}

/** Todas las excepciones a DEFAULT_MIN_AGE, para la Política de privacidad. */
export function ageTable(): { code: string; alone: number; withGuardian: number | null }[] {
  const codes = new Set([...Object.keys(CONSENT_AGE), ...Object.keys(WITH_GUARDIAN_FROM)]);
  return [...codes].map((code) => ({ code, ...ageRuleFor(code) }));
}
